/**
 * URL hygiene for the launcher icon (`branding.launcherIconUrl` /
 * `data-launcher-icon`).
 *
 * This value differs in provenance from the backend-supplied image URLs
 * guarded by `safeImageUrl()`: it comes either from an entity preset we
 * author, or from the host site's own operator in their own `<script>`
 * tag on a page they already control. A site-relative path
 * (`/wp-content/uploads/mark.png`) is therefore both safe and the shape
 * an embedder is most likely to write — so it is resolved against the
 * page URL rather than rejected.
 *
 * The scheme check is NOT relaxed. After resolution the URL must still be
 * `http:` or `https:`, so `javascript:`, `data:`, `blob:`, `file:`, and
 * `vbscript:` remain rejected exactly as in `safeImageUrl()`.
 *
 * ## The `{apiEndpoint}` token
 *
 * Entity presets ship an icon we host ourselves, next to the widget
 * bundle. That URL is environment-dependent — UAT and production are
 * different hosts — so a preset cannot hardcode it, and a bare relative
 * path would resolve against the HOST SITE (dermatologycollective.sg),
 * not against us. Presets therefore write the explicit token:
 *
 *     launcherIconUrl: "{apiEndpoint}/widget/dccs-launcher.png"
 *
 * which is substituted with the origin of the embed's own `data-api`
 * value, so a UAT embed loads the UAT asset and a production embed loads
 * the production one, with no per-environment preset variants.
 *
 * The token is explicit precisely so the two relative-resolution
 * semantics can never be confused: a value WITHOUT the token always
 * resolves against the host page (what an embedder means by
 * `/wp-content/...`), and a value WITH it always resolves against our
 * own server (what a preset means by `/widget/...`).
 */

import { safeImageUrl } from "./messages/safeImageUrl";

/** Placeholder substituted with the embed's API origin. */
export const API_ENDPOINT_TOKEN = "{apiEndpoint}";

export interface LauncherIconResolveOptions {
  /**
   * The embed's `data-api` value. Required only when the icon URL carries
   * `{apiEndpoint}`; its origin replaces the token (any path such as
   * `/api` is stripped, matching the embed contract).
   */
  apiEndpoint?: string;
  /**
   * Page URL that non-token relative paths resolve against. Defaults to
   * the document's base URI; injectable for tests.
   */
  baseUrl?: string;
}

/**
 * Resolve a launcher-icon URL to an absolute http(s) URL, or `null` if it
 * is absent, blank, unresolvable, or uses a disallowed scheme. A `null`
 * return makes the launcher fall back to its built-in glyph.
 */
export function resolveLauncherIconUrl(
  raw: string | null | undefined,
  options: LauncherIconResolveOptions = {},
): string | null {
  if (typeof raw !== "string") return null;
  let candidate = raw.trim();
  if (!candidate) return null;

  if (candidate.includes(API_ENDPOINT_TOKEN)) {
    const origin = apiOrigin(options.apiEndpoint);
    // No usable API endpoint means the token cannot be resolved. Returning
    // null here degrades to the built-in glyph rather than requesting a
    // literal "{apiEndpoint}/..." URL that would 404 on the host site.
    if (!origin) return null;
    candidate = candidate.split(API_ENDPOINT_TOKEN).join(origin);
  }

  // Already absolute (and correctly-schemed)? Use it verbatim.
  const absolute = safeImageUrl(candidate);
  if (absolute) return absolute;

  const base =
    options.baseUrl ??
    (typeof document !== "undefined" ? document.baseURI : undefined);
  if (!base) return null;

  // Resolve site-relative / scheme-relative forms against the host page,
  // then re-apply the same scheme gate. `javascript:alert(1)` parses with
  // a `javascript:` protocol here and is rejected by safeImageUrl below.
  let resolved: string;
  try {
    resolved = new URL(candidate, base).href;
  } catch {
    return null;
  }
  return safeImageUrl(resolved);
}

/**
 * Origin of the embed's API endpoint, or `null` when it is absent or
 * unparseable. Mirrors the embed contract documented for `data-api`:
 * any path on the URL is stripped, so `https://host/api` and
 * `https://host/` both yield `https://host`.
 */
function apiOrigin(apiEndpoint: string | undefined): string | null {
  if (typeof apiEndpoint !== "string") return null;
  const trimmed = apiEndpoint.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.origin;
  } catch {
    return null;
  }
}
